export function toHtmlColor(color: number) {
  return '#' + color.toString(16).padStart(6, '0')
}

export function stringifyThemeObject(colorObject: Record<string, number>) {
  return Object.entries(colorObject)
    .map(([key, value]) => `${key}:${value.toString(16).padStart(6, '0')}`)
    .join(',')
}

export function parseThemeObject(input: string) {
  let result: Theme = { ...defaultTheme }

  input.split(',').forEach((pair) => {
    let [name, value] = pair.split(':')
    result[name] = parseInt(value, 16)
  })

  return result
}

// Luminosity-inspired theme - clean, modern look
export const defaultTheme = {
  background: 0x1a2634,      // Dark blue-gray background
  rail: 0x8b9caa,            // Light gray-blue rails (was pavement)
  railInner: 0x2d3d4d,       // Dark inner rail color (was asphalt)
  pavement: 0x8b9caa,        // Keep for compatibility
  asphalt: 0x2d3d4d,         // Keep for compatibility
  departure: 0x4a5568,       // Muted gray for departure station
  switch: 0xf0f4f8,          // Light switch color
  switchHover: 0xb8c9d9,     // Hover state
  switchOutline: 0xe2e8f0,   // Switch outline
  scoreBackground: 0x2d3d4d, // Score area background
  switchWithTrain: 0x1a2634, // Switch when train is present
  switchHoverWithTrain: 0x1a2634,
}

export type Theme = typeof defaultTheme

export function stringifyColorList(colorList: number[]) {
  return colorList.map((c) => c.toString(16).padStart(6, '0')).join(',')
}

export function parseColorList(input: string) {
  return input.split(',').map((w) => parseInt(w, 16))
}

// Maximally distinct colors for high contrast at all levels
// Colors are ordered to maximize perceptual difference between adjacent colors
// This helps especially at higher levels (10+ stations)
export const defaultColorList = [
  0x2563eb, // Blue (strong primary)
  0xdc2626, // Red (strong primary)
  0x16a34a, // Green (strong primary)
  0xfbbf24, // Yellow/Gold (bright, distinct)
  0x9333ea, // Purple (distinct from blue)
  0xf97316, // Orange (distinct from red/yellow)
  0x06b6d4, // Cyan (distinct from blue/green)
  0xdb2777, // Magenta/Pink (distinct from red/purple)
  0x65a30d, // Lime green (distinct from green)
  0x7c3aed, // Violet (between blue and purple)
  0xea580c, // Dark orange/burnt (distinct from orange)
  0x0891b2, // Dark cyan/teal (distinct from cyan)
  0xbe185d, // Dark pink/rose (distinct from magenta)
  0x4f46e5, // Indigo (between blue and violet)
  0x15803d, // Forest green (distinct from green/lime)
]

// Icon patterns to help distinguish colors at high levels
// Each station can have a different icon shape/pattern
export const stationPatterns = [
  'solid',      // Plain fill
  'stripe',     // Horizontal stripe
  'dot',        // Center dot
  'ring',       // Ring/outline emphasis
  'diamond',    // Diamond shape inside
  'cross',      // Cross/plus pattern
  'triangle',   // Triangle marker
  'square',     // Square marker inside
]

export function getPatternForIndex(colorIndex: number): string {
  // Use patterns to further differentiate at higher levels
  // First 8 colors use solid, then patterns cycle
  if (colorIndex < 8) return 'solid'
  return stationPatterns[(colorIndex - 8) % stationPatterns.length]
}

export function getColorFromList(colorIndex: number, colorList: number[]) {
  return colorList[colorIndex % colorList.length]
}

// Get secondary color for dual-color trains/houses
// Currently disabled - all items are single color
export function getSecondaryColor(colorIndex: number, colorList: number[]): number | null {
  // Return null for single-color mode (all items single color)
  return null
}

export function interpolateColor(a: number, z: number) {
  return (progress: number) => {
    const ar = (a >> 16) & 0xff
    const ag = (a >> 8) & 0xff
    const ab = a & 0xff

    const zr = (z >> 16) & 0xff
    const zg = (z >> 8) & 0xff
    const zb = z & 0xff

    const r = Math.round(ar + (zr - ar) * progress)
    const g = Math.round(ag + (zg - ag) * progress)
    const b = Math.round(ab + (zb - ab) * progress)

    return (r << 16) | (g << 8) | b
  }
}
